import { Cancel, CheckCircle } from "@clockwise/design-system/icons";
import { PublicSharedProposal } from "@clockwise/schema/v2";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import { AvatarStack } from "@clockwise/web-commons/src/ui/avatar-stack/AvatarStack";
import Tooltip from "@material-ui/core/Tooltip";
import { isEmpty } from "lodash";
import { DateTime } from "luxon";
import { MagicWand, Warning } from "phosphor-react";
import React from "react";
import { loginUser } from "../../mutations";
import { LoginBtn } from "../login-btn";
import { formattedDateTime } from "../web-app-calendar/calendar-popover/utils/formattedDateTime";
import { ProposalOwner } from "./ProposalOwner";
import { LoginInProgressViaType } from "./SharedProposalRendererGateway";

type PublicSharedProposalActiveProps = {
  proposal: PublicSharedProposal;
  loginInProgressVia: LoginInProgressViaType;
  onLoginStart: (button: string) => void;
  onLoginSuccess: () => void;
  onLoginFailure: () => void;
  onGoogleFailure: () => void;
};

const getTimeText = (timeInterval: string | null | undefined, timezone: string) => {
  if (!timeInterval) return null;

  return formattedDateTime(DateTime.fromISO(timeInterval.split("/")[0]), timezone, true, true);
};

const durationText = (duration: number) => {
  if (duration >= 60) {
    const hours = (duration / 60).toFixed(2);
    return `${hours} ${duration > 60 ? "hours" : "hour"}`;
  } else {
    return `${duration} minutes`;
  }
};

export const PublicSharedProposalActive = ({
  proposal,
  loginInProgressVia,
  onLoginStart,
  onLoginSuccess,
  onLoginFailure,
  onGoogleFailure,
}: PublicSharedProposalActiveProps) => {
  const redirect = `app/proposal/${proposal.proposalId}`;

  const eventSummary = proposal.eventSummary;
  // TODO: Ask BE to return a type for eventSummary
  const isReschedule = !isEmpty(eventSummary.oldTime);
  const title = eventSummary.title;
  const startTimeFormatted = getTimeText(eventSummary.time, proposal.ownerTimeZone);
  const oldTimeFormatted =
    isReschedule && getTimeText(eventSummary.oldTime, proposal.ownerTimeZone);
  const attendees = eventSummary.attendees;
  const hasFixableConflicts = !isEmpty(proposal.consequencesSummary);

  let titleBody = `${proposal.owner.profile.givenName} is `;
  titleBody += isReschedule ? " rescheduling a meeting" : " inviting you to a meeting";
  titleBody += ", and needs your input";
  titleBody += hasFixableConflicts ? " to fix a conflict." : ".";

  return (
    <>
      <ProposalOwner owner={proposal.owner} titleBody={titleBody} />
      <div className="cw-flex-col cw-my-2">
        <div
          className="cw-w-full cw-rounded-xl cw-drop-shadow-sm cw-border cw-border-solid cw-border-muted
              cw-group focus:cw-border-brand-emphasis-pressed cw-body-base cw-overflow-hidden cw-bg-default
              hover:cw-border-brand-emphasis-hover cw-p-3"
        >
          <div className="cw-font-semibold">{title}</div>
          {startTimeFormatted && <div>{startTimeFormatted}</div>}
          {oldTimeFormatted && <div className="cw-line-through">{oldTimeFormatted}</div>}
          {!isEmpty(attendees) && (
            <>
              <AvatarStack size="small" overlap maxShown={5}>
                {attendees.map((attendee, index) => {
                  return <AttendeeAvatar key={index} profile={attendee.profile} size="medium" />;
                })}
              </AvatarStack>
              <span>
                {" | "}
                {durationText(eventSummary.durationInMinutes)}
              </span>
            </>
          )}
        </div>
      </div>
      {hasFixableConflicts && (
        <div className="cw-mt-2 cw-flex cw-flex-row cw-p-3">
          <div className="cw-flex cw-items-center">
            <MagicWand color="#4459dd" weight="fill" size="14px" />
          </div>
          <div className="cw-flex cw-items-center cw-ml-2">{proposal.consequencesSummary}</div>
        </div>
      )}
      {!isEmpty(proposal.tradeoffsSummary) && (
        <div className="cw-mt-2 cw-flex cw-flex-row cw-p-3">
          <div className="cw-flex cw-items-center">
            <Warning color="#DC6803" weight="fill" size="14px" />
          </div>
          <div className="cw-flex cw-items-center cw-ml-2">{proposal.tradeoffsSummary}</div>
        </div>
      )}
      <div className="cw-flex cw-flex-col cw-w-full cw-mt-3 cw-gap-2">
        <div className="cw-pt-3 cw-border-t cw-border-solid cw-border-t-muted cw-font-semibold">
          Reply to {proposal.owner.profile.givenName}
        </div>
        <div className="cw-p-3 cw-bg-neutral">
          Clockwise may be able to help fix your conflict automatically if you sign-in.{" "}
          <Tooltip
            title="When there are no open slots, Clockwise will scan calendars it has access to 
                  and find which slots could work by moving meetings to 
                  other times that work for all their attendees."
          >
            <span className="cw-underline cw-decoration-dashed">Tell me more</span>
          </Tooltip>
          <div className="cw-mt-2">
            {loginInProgressVia === "Google" ? (
              <CircularProgress />
            ) : (
              <LoginBtn
                label="Sign in with Google"
                variant={"googleSimple"}
                loginUserMutation={loginUser}
                redirect={redirect}
                onStart={() => onLoginStart("Google")}
                onSuccess={onLoginSuccess}
                onFailure={onLoginFailure}
                onGoogleFailure={onGoogleFailure}
                sentiment={"info"}
                size={"jumbo"}
              />
            )}
          </div>
        </div>
        {loginInProgressVia === "Accept" ? (
          <CircularProgress />
        ) : (
          <LoginBtn
            label="Accept"
            variant={"default"}
            loginUserMutation={loginUser}
            redirect={redirect}
            onStart={() => onLoginStart("Accept")}
            onSuccess={onLoginSuccess}
            onFailure={onLoginFailure}
            onGoogleFailure={onGoogleFailure}
            textAlign="left"
            icon={CheckCircle}
            sentiment="positive"
            size={"jumbo"}
          />
        )}
        {loginInProgressVia === "Decline" ? (
          <CircularProgress />
        ) : (
          <LoginBtn
            label="Decline"
            variant={"default"}
            loginUserMutation={loginUser}
            redirect={redirect}
            onStart={() => onLoginStart("Decline")}
            onSuccess={onLoginSuccess}
            onFailure={onLoginFailure}
            onGoogleFailure={onGoogleFailure}
            textAlign="left"
            icon={Cancel}
            sentiment="destructive"
            size={"jumbo"}
          />
        )}
      </div>
    </>
  );
};
